import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import moment from 'moment';

import { getOriginalMonthlyPriceInCents } from '../../../../../CoachTools/Model/UpsellConfig';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import { formatCurrencyCents } from '../../../../../utils/formatters';
import { DateFormat } from '../../../../../utils/date';
import PaymentConfirmedBanner from '../PaymentConfirmedBanner';
import SubmitButton from '../SubmitButton';
import texts from './texts.json';
import {
  Container,
  OfferCard,
  CoachMedia,
  CoachImage,
  CoachCaption,
  OfferBody,
  Overline,
  Title,
  Description,
  PriceRow,
  Price,
  OriginalPrice,
  PriceUnit,
  Actions,
  DeclineButton,
  Fineprint,
} from './styles';

const Step = {
  OFFER: 'offer',
  ADDED: 'added',
  FAILED: 'failed',
};

/**
 * The coach's 1:1 offered right after the challenge is paid (the upsell test's control arm): full screen on a
 * phone, a two-column card on desktop (one centered column for the answer's outcome), under a banner confirming
 * the payment. The price is struck through from the offer's original price when it has one. Adding it signs the
 * client up with the card they just paid with; nothing is charged until the challenge ends. Either answer ends in
 * `onDone`.
 */
const PostPaymentUpsellOffer = ({
  offer,
  paidAmount,
  challengeName,
  challengeEndDate,
  coachName,
  coachImageUrl,
  isDesktop,
  isDarkTheme,
  color,
  onAccept,
  onDecline,
  onShown,
  onDone,
}) => {
  const isComponentMountedRef = useComponentMounted();
  const [step, setStep] = useState(Step.OFFER);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    onShown();
  }, [onShown]);

  const price = formatCurrencyCents(offer.monthlyPriceInCents, offer.currency);
  const originalPriceInCents = getOriginalMonthlyPriceInCents(offer);
  // The outcome has no coach photo beside it, so it takes one column, centered.
  const isSplit = isDesktop && step === Step.OFFER;
  const startDate = moment(challengeEndDate).format(DateFormat.MONTH_DAY_FORMAT);
  const minMonths = offer.minSubscriptionMonths || 1;

  const handleAccept = useCallback(async () => {
    setIsAdding(true);
    const isAdded = await onAccept();
    if (isComponentMountedRef.current) {
      setIsAdding(false);
      setStep(isAdded ? Step.ADDED : Step.FAILED);
    }
  }, [onAccept, isComponentMountedRef]);

  const handleDecline = useCallback(() => {
    onDecline();
    onDone();
  }, [onDecline, onDone]);

  const renderOutcome = (title, description) => (
    <OfferBody $isCentered>
      <Title $isDarkTheme={isDarkTheme}>{title}</Title>
      <Description>{description}</Description>
      <SubmitButton type="button" onClick={onDone} $background={color} $isDarkTheme={isDarkTheme}>
        {texts.continue}
      </SubmitButton>
    </OfferBody>
  );

  return (
    <Container $isDesktop={isSplit}>
      <PaymentConfirmedBanner amount={paidAmount} productName={challengeName} isDarkTheme={isDarkTheme} />
      <OfferCard $isDesktop={isSplit} $isDarkTheme={isDarkTheme} aria-label={texts.title}>
        {step === Step.OFFER && (
          <>
            <CoachMedia $isDesktop={isDesktop}>
              {!!coachImageUrl && <CoachImage src={coachImageUrl} alt={coachName} />}
              <CoachCaption>{coachName}</CoachCaption>
            </CoachMedia>
            <OfferBody>
              <Overline $isDarkTheme={isDarkTheme}>{texts.overline}</Overline>
              <Title $isDarkTheme={isDarkTheme}>{texts.title}</Title>
              <Description>{format(texts.description, { challengeName, coachName })}</Description>
              <PriceRow>
                {!!originalPriceInCents && (
                  <OriginalPrice>{formatCurrencyCents(originalPriceInCents, offer.currency)}</OriginalPrice>
                )}
                <Price $isDarkTheme={isDarkTheme}>{price}</Price>
                <PriceUnit>{texts.perMonth}</PriceUnit>
              </PriceRow>
              <Actions>
                <SubmitButton
                  type="button"
                  isLoading={isAdding}
                  onClick={handleAccept}
                  $background={color}
                  $isDarkTheme={isDarkTheme}
                >
                  {texts.accept}
                </SubmitButton>
                <DeclineButton type="button" disabled={isAdding} onClick={handleDecline} $isDarkTheme={isDarkTheme}>
                  {texts.decline}
                </DeclineButton>
                <Fineprint>
                  {format(texts.fineprint, { price, startDate })}
                  {/* The minimum accepting binds the client to, when it is longer than a month. */}
                  {minMonths > 1 && ` ${format(texts.minimum, { months: minMonths })}`}
                </Fineprint>
              </Actions>
            </OfferBody>
          </>
        )}
        {step === Step.ADDED && renderOutcome(
          texts.addedTitle,
          format(texts.addedDescription, { startDate, challengeName }),
        )}
        {step === Step.FAILED && renderOutcome(texts.failedTitle, format(texts.failedDescription, { coachName }))}
      </OfferCard>
    </Container>
  );
};

PostPaymentUpsellOffer.propTypes = {
  offer: PropTypes.shape({
    monthlyPriceInCents: PropTypes.number.isRequired,
    originalMonthlyPriceInCents: PropTypes.number,
    minSubscriptionMonths: PropTypes.number,
    currency: PropTypes.string,
  }).isRequired,
  paidAmount: PropTypes.string.isRequired,
  challengeName: PropTypes.string.isRequired,
  challengeEndDate: PropTypes.string.isRequired,
  coachName: PropTypes.string.isRequired,
  coachImageUrl: PropTypes.string,
  isDesktop: PropTypes.bool,
  isDarkTheme: PropTypes.bool,
  color: PropTypes.string,
  // Resolves whether the client is now signed up for the 1:1.
  onAccept: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
  onShown: PropTypes.func,
  onDone: PropTypes.func.isRequired,
};

PostPaymentUpsellOffer.defaultProps = {
  coachImageUrl: '',
  isDesktop: false,
  isDarkTheme: false,
  color: '',
  onShown: () => {},
};

export default PostPaymentUpsellOffer;
